import BaseObject from "./models/BaseObject";
import type BaseModel from "./models/BaseModel";
import type { AnalyserConnection } from "./connectionAnalyser/VirtualConnections";

/** Keeps displayed objects alive independently of the current action selection. Not persisted or undone. */
export class DisplaySelections extends BaseObject {
  override get className(): string {
    return "DisplaySelections";
  }

  private entries = new Map<string, {object: BaseModel; dispose: () => void}>();
  private connectionTargets = new Map<string, BaseModel>();
  private explicitNavigatorObjects = false;

  constructor() { super({storeInObjectRegistry: false, isPersistent: false}); }

  get(slot: string): BaseModel | undefined { return this.entries.get(slot)?.object; }

  /** Adds a selected object's path without replacing previously displayed navigator objects. */
  addNavigatorObject(object: BaseModel): void {
    this.explicitNavigatorObjects = false;
    this.set(`navigator:${object.id}`, object);
  }

  /** Replaces navigator targets while retaining independent editor owners. */
  setNavigatorObjects(objects: readonly BaseModel[]): void {
    this.explicitNavigatorObjects = true;
    for (const slot of this.entries.keys()) {
      if (slot === "navigator" || slot.startsWith("navigator:") || slot.startsWith("navigatorConnection:")) {
        this.remove(slot, false);
      }
    }
    this.connectionTargets.clear();
    for (const object of objects) this.set(`navigator:${object.id}`, object, false);
    this.raiseOnChange("navigator");
  }

  /** Returns the navigator's complete display path without modifying other display owners. */
  getNavigatorPath(): readonly BaseModel[] {
    const result: BaseModel[] = [];
    const visited = new Set<BaseObject>();
    for (const [slot, entry] of this.entries) {
      if (slot !== "navigator" && !slot.startsWith("navigator:") && !slot.startsWith("navigatorConnection:")) continue;
      for (let object: BaseObject | undefined = entry.object; object && !visited.has(object); object = object.parent) {
        visited.add(object);
        if (object.isBaseModel && !object.isDeleted) result.push(object as BaseModel);
      }
    }
    return result;
  }

  /** Truncates the display path at a collapsed node, keeping that node and its ancestors visible. */
  collapseNavigatorPath(object: BaseModel): void {
    let changed = false;
    for (const [slot, entry] of this.entries) {
      if (slot !== "navigator" && !slot.startsWith("navigator:") && !slot.startsWith("navigatorConnection:")) continue;
      const visited = new Set<BaseObject>();
      for (let parent = entry.object.parent; parent && !visited.has(parent); parent = parent.parent) {
        visited.add(parent);
        if (parent === object) {
          this.set(slot, object, false);
          changed = true;
          break;
        }
      }
    }
    if (changed) this.raiseOnChange("navigator");
  }

  /** Adds actuator paths until deselection, preserving branches explicitly collapsed by the user. */
  setConnectionTargets(connections: readonly AnalyserConnection[], selectedObject?: BaseModel): void {
    // Explicit editor clicks show their chosen consumers rather than automatic actor paths.
    if (this.explicitNavigatorObjects) return;
    const targets = new Map<string, BaseModel>();
    for (const connection of connections) {
      if (connection.isDeleted) continue;
      for (const device of [connection.fromDevice, connection.toDevice]) {
        if (!device?.isActorChannel || device.isDeleted) continue;
        // Selecting a container must not open its own connected children.
        const ancestors = new Set<BaseObject>();
        for (let parent = device?.parent; parent && !ancestors.has(parent); parent = parent.parent) ancestors.add(parent);
        if (selectedObject && ancestors.has(selectedObject)) continue;
        targets.set(device.id, device);
      }
    }
    let changed = false;
    for (const [id, target] of this.connectionTargets) {
      if (target.isDeleted) {
        this.remove(`navigatorConnection:${id}`, false);
        this.connectionTargets.delete(id);
        changed = true;
      }
    }
    for (const [id, target] of targets) {
      if (this.connectionTargets.get(id) !== target || !this.entries.has(`navigatorConnection:${id}`)) {
        this.set(`navigatorConnection:${id}`, target, false);
        this.connectionTargets.set(id, target);
        changed = true;
      }
    }
    if (changed) this.raiseOnChange("navigator");
  }

  /** Replaces one display's owner and observes its deletion, including deletion through a parent. */
  set(slot: string, object: BaseModel, notify = true): void {
    if (object.isDeleted || this.get(slot) === object) return;
    this.entries.get(slot)?.dispose();
    const subscription = object.onChange.subscribe(() => {
      if (object.isDeleted) this.remove(slot);
    });
    this.entries.set(slot, {object, dispose: () => object.onChange.unsubscribe(subscription)});
    if (notify) this.raiseOnChange(slot);
  }

  /** Closes one display without changing the action selection. */
  remove(slot: string, notify = true): void {
    const entry = this.entries.get(slot);
    if (!entry) return;
    entry.dispose();
    this.entries.delete(slot);
    if (notify) this.raiseOnChange(slot);
  }

  /** Releases display owners, optionally retaining editors needed after an action clears selection. */
  clear(preserveSlots: readonly string[] = []): void {
    this.explicitNavigatorObjects = false;
    for (const slot of this.entries.keys()) {
      if (!preserveSlots.includes(slot)) this.remove(slot, false);
    }
    this.connectionTargets.clear();
    this.raiseOnChange();
  }
}
